import React, { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useAuth } from '@/context/AuthContext';
import DailyNervView from './DailyNervView';
import DailyNervLiveView from './DailyNervLiveView';

// Daily NERV Trends source (FIG-238 follow-up): everyone sees the Variant History snapshots (the default).
// Admins also get a toggle to the calculated chart, to compare the two. The choice is not saved: it starts
// on the snapshots every time the Overview opens, and each merchant starts there too (keyed by the caller).

type NervSource = 'snapshots' | 'calculated';

interface DailyNervTrendsProps {
  organisation: { id?: string } | null | undefined;
  /** Opens the merchant's Settings tab (the calculated chart's "Change in Settings" link). */
  onOpenSettings?: () => void;
}

const DailyNervTrends: React.FC<DailyNervTrendsProps> = ({ organisation, onOpenSettings }) => {
  const { isAdmin } = useAuth();
  const [source, setSource] = useState<NervSource>('snapshots');

  if (!isAdmin) return <DailyNervView organisation={organisation} />;

  return (
    <div className="space-y-3" data-testid="daily-nerv-trends">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">NERV source</span>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={source}
          // A click on the selected item reports an empty value; keep the current source.
          onValueChange={(value) => { if (value === 'snapshots' || value === 'calculated') setSource(value); }}
          aria-label="NERV source"
        >
          <ToggleGroupItem value="snapshots" aria-label="Variant History snapshots">Variant History snapshots</ToggleGroupItem>
          <ToggleGroupItem value="calculated" aria-label="Calculated">Calculated</ToggleGroupItem>
        </ToggleGroup>
        <span className="text-xs text-muted-foreground">Admin only</span>
      </div>
      {source === 'snapshots'
        ? <DailyNervView organisation={organisation} />
        : <DailyNervLiveView organisation={organisation} onOpenSettings={onOpenSettings} />}
    </div>
  );
};

export default DailyNervTrends;
